<template>
  <!-- MBR037 Bundle Detail — member_app_UI/song_fa_members_voucher_bundle_detail -->
  <div class="page-header align-items-start mbr-bundle">
    <span class="mask bg-white"></span>
    <!-- The header renders straight away so back + title hold still while the bundle loads;
         only the body waits for the API. -->
    <div class="container p-0">
      <header class="mbr-bundle__bar">
        <button type="button" class="mbr-bundle__back" :aria-label="$t('common.back')" @click="landing()">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 class="mbr-bundle__title">{{ voucherBundleDetail.title ?? "" }}</h1>
        <span class="mbr-bundle__spacer" aria-hidden="true"></span>
      </header>

      <main v-if="isShowLayout" class="mbr-bundle__body">
        <section class="mbr-bundle__summary">
          <div class="mbr-bundle__head">
            <img :src="`${voucherBundleDetail.icon_url}`" class="mbr-bundle__icon" alt="" />
            <div class="mbr-bundle__head-text">
              <span class="mbr-bundle__pill">{{ $t('voucher.value_bundle') }}</span>
              <h2 class="mbr-bundle__name">{{ voucherBundleDetail.title ?? "" }}</h2>
              <p class="mbr-bundle__meta">
                {{ validityText(voucherBundleDetail.expiry_date_text) }}
              </p>
            </div>
          </div>

          <div class="mbr-bundle__totals">
            <div class="mbr-bundle__totals-text">
              <span class="mbr-bundle__label">{{ $t('voucher.total_package') }}</span>
              <strong class="mbr-bundle__count">
                {{ $t('voucher.n_included', { text: voucherCountText(voucherBundleDetail) }) }}
              </strong>
            </div>

            <!-- Owned bundle: how many of the member's vouchers are still unused.
                 The API only fills voucher_in_bundle_* when is_reward is true. -->
            <div v-if="isReward" class="mbr-bundle__ring-wrap">
              <div class="mbr-bundle__ring">
                <svg viewBox="0 0 36 36" aria-hidden="true">
                  <path
                    class="mbr-bundle__ring-track"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3.5"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    class="mbr-bundle__ring-fill"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="3.5"
                    stroke-linecap="round"
                    :stroke-dasharray="ringDash"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <span class="mbr-bundle__ring-value">{{ availableCount }}</span>
              </div>
              <div>
                <div class="mbr-bundle__ring-left">{{ $t('voucher.n_left', { n: availableCount }) }}</div>
                <div class="mbr-bundle__ring-used">{{ $t('voucher.n_used', { n: spentCount }) }}</div>
              </div>
            </div>

            <!-- Bundle still on sale: the exchange price, same conditions as before -->
            <div v-else class="mbr-bundle__price">
              <div
                v-if="merchantMember.reward_type == 1 || voucherBundleDetail.is_bundle"
                class="mbr-bundle__price-amount"
              >
                {{ voucherBundleDetail.amount_text }}
              </div>
              <div
                v-if="
                  (merchantMember.reward_type == 2 && !voucherBundleDetail.is_bundle) ||
                  (merchantMember.reward_type == 2 &&
                    voucherBundleDetail.is_bundle &&
                    voucherBundleDetail.points_convert > 0)
                "
                class="mbr-bundle__price-points"
              >
                <img :src="voucherBundleDetail.point_icon" alt="" height="18" width="20" />
                <span>{{ voucherBundleDetail.point_text }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- Owned bundle: the member's own vouchers, split by whether they are still usable -->
        <template v-if="isReward">
          <nav class="mbr-bundle__tabs" :aria-label="$t('voucher.voucher_status')">
            <button
              type="button"
              class="mbr-bundle__tab"
              :class="{ 'mbr-bundle__tab--active': type == 1 }"
              @click="changeType(1)"
            >
              {{ $t('voucher.available_n', { n: availableCount }) }}
            </button>
            <button
              type="button"
              class="mbr-bundle__tab"
              :class="{ 'mbr-bundle__tab--active': type == 2 }"
              @click="changeType(2)"
            >
              {{ $t('voucher.used_expired_n', { n: spentCount }) }}
            </button>
          </nav>

          <section class="mbr-bundle__section">
            <span class="mbr-bundle__section-label">
              {{ type == 1 ? $t('voucher.ready_to_redeem') : $t('voucher.no_longer_usable') }}
            </span>

            <div v-if="myRewards.length" class="mbr-bundle__list">
              <div
                v-for="(value, index) in myRewards"
                :key="index"
                class="mbr-bundle__voucher"
                @click="getRewardDetail(value.member_reward_id, value.expiry_date_text)"
              >
                <span class="mbr-bundle__voucher-no">#{{ index + 1 }}</span>
                <div class="mbr-bundle__voucher-text">
                  <h4 class="mbr-bundle__voucher-name">{{ value.reward_title ?? "" }}</h4>
                  <p class="mbr-bundle__voucher-valid">
                    {{ $t('voucher.valid_until', { date: value.expiry_date_text }) }}
                  </p>
                </div>
                <span v-if="value.locked" class="mbr-bundle__tag mbr-bundle__tag--locked">
                  {{ $t('voucher.locked') }}
                </span>
                <span v-else-if="value.use" class="mbr-bundle__tag">{{ $t('voucher.used') }}</span>
                <span v-else-if="value.isExpired" class="mbr-bundle__tag">{{ $t('voucher.expired') }}</span>
                <svg
                  v-else
                  class="mbr-bundle__chevron"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  aria-hidden="true"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>

            <div v-else class="mbr-bundle__empty">
              <span class="mbr-bundle__empty-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </span>
              <h4 class="mbr-bundle__empty-title">{{ $t('voucher.bundle_empty_title') }}</h4>
              <p class="mbr-bundle__empty-text">
                {{
                  type == 1
                    ? $t('voucher.bundle_empty_available')
                    : $t('voucher.bundle_empty_used')
                }}
              </p>
            </div>
          </section>
        </template>

        <!-- What the bundle is made of; the only voucher breakdown available before exchange -->
        <section
          v-if="voucherBundleDetail.number_of_vouchers.total > 0"
          class="mbr-bundle__card"
        >
          <h3 class="mbr-bundle__heading">{{ $t('voucher.whats_inside') }}</h3>
          <ul class="mbr-bundle__inside">
            <li
              v-for="(voucher, index) in voucherBundleDetail.number_of_vouchers.detail"
              :key="index"
            >
              <span class="mbr-bundle__inside-name">{{ voucher.title }}</span>
              <span class="mbr-bundle__inside-qty">&times;{{ voucher.total }}</span>
            </li>
          </ul>
        </section>

        <section v-if="terms.length" class="mbr-bundle__card">
          <h3 class="mbr-bundle__heading">{{ $t('reward.terms_conditions') }}</h3>
          <ul class="mbr-bundle__terms">
            <li v-for="(value, index) in terms" :key="index">{{ value }}</li>
          </ul>
        </section>
      </main>

      <div
        v-if="isShowLayout && !isReward && !voucherBundleDetail.is_disabled_btn"
        class="mbr-bundle__cta"
      >
        <button
          type="button"
          class="mbr-bundle__exchange"
          @click="exchange()"
          data-bs-toggle="modal"
          data-bs-target="#bottom_modal"
        >
          {{ $t('reward.exchange') }}
        </button>
      </div>
    </div>
    <!-- Modal -->
    <Exchange
      :rewardPerTransaction="merchantMember.reward_type ?? 0"
      :voucherData="voucherBundleDetail"
      :amount_format="merchantMember.amount_format"
      :amount_in_account="merchantMember.amount_in_account"
      :point_format="merchantMember.point_format"
      :point_in_account="merchantMember.point"
      :is_disabled="voucherBundleDetail.is_disabled"
      :is_disabled_by_card="voucherBundleDetail.is_disabled_by_card"
      :confirm-exchange="confirmExchange"
      :keyReload="keyReloadExchange"
    />

    <div class="position-fixed top-1 end-1 z-index-2">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title
        date
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
    <ModalConfirm
      :message="$t('reward.confirm_exchange')"
      :btnYes="$t('common.yes')"
      :sycn-profile="processExchange"
    />
    <ModalSuccess
      :processAcction="gotoMyReward"
      :message="$t('reward.exchange_success')"
      :buttonText="$t('reward.go_to_my_reward')"
    />
    <QrcodeReward
      :rewardDetail="rewardDetail"
      :merchantMember="merchantMember"
      :isShowQr="typeChecked == 2 ? false : true"
    />
  </div>
</template>

<script>
import { mapMutations, mapActions } from "vuex";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import RewardService from "../services/reward.service";
import "bootstrap/dist/js/bootstrap.bundle";
import Exchange from "@/components/Exchange.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import ModalSuccess from "@/components/ModalSuccess.vue";
import $ from "jquery";
import QrcodeReward from "@/components/QrcodeReward.vue";
import ModalConfirm from "@/components/ModalConfirm.vue";
import { getMerchantCode } from "@/config/utils";
import { voucherCountText, validityText } from "@/i18n/apiText";
import { errorMessage } from "@/utils/errorMessage";

export default {
  name: "voucher-bundle-detail",
  components: {
    MaterialSnackbar,
    Exchange,
    MaterialLoading,
    ModalSuccess,
    QrcodeReward,
    ModalConfirm
  },
  data() {
    return {
      voucherBundleDetail: {},
      merchantMember: {},
      loaderActive: false,
      snackbar: null,
      isShowLayout: false,
      merchantCode: "",
      myRewards: [],
      rewardsUsed: [],
      rewardsUnused: [],
      rewardDetail: {},
      isReward: this.$route.query.isReward ?? false,
      memberVoucherBundleId: this.$route.query.memberVoucherBundleId ?? 0,
      type: 1,
      keyReloadExchange: 'keyReloadExchange'
    };
  },
  computed: {
    // Vouchers the member still holds in this bundle. Both lists come back empty
    // unless the screen was opened with isReward, so the ring only shows there.
    availableCount() {
      return this.rewardsUnused.length;
    },

    spentCount() {
      return this.rewardsUsed.length;
    },

    // stroke-dasharray on a 100-unit path: the unused share of the bundle
    ringDash() {
      const total = this.availableCount + this.spentCount;
      const filled = total ? (this.availableCount / total) * 100 : 0;
      return `${filled}, 100`;
    },

    // terms_arr keeps the empty pieces left by splitting on <br>
    terms() {
      return (this.voucherBundleDetail.terms_arr ?? []).filter(Boolean);
    },
  },
  created() {
    var session_id = this.$route.query.session_id;
    var id = this.$route.params.id;

    if (session_id && id) {
      // call back after pay success - stripe
      this.showLoader();
      this.isLoadDone = false;
      RewardService.processExchangeVoucherBundleByCard(id, session_id).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.getVoucherBundleDetail();
            $(".btn-success-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.isLoadDone = true;
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    } else {
      this.getVoucherBundleDetail();
    }
  },
  // draws its own back header like the design, same chrome as rewardDetail
  beforeMount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.add("mbr-sidenav-overlay");
    app.classList.remove("g-sidenav-pinned");
  },

  beforeUnmount() {
    this.toggleNavbarOnly();
    this.toggleHideConfig();
    const app = document.querySelector("#app");
    app.classList.remove("mbr-sidenav-overlay");
    if (window.innerWidth > 1200) {
      app.classList.add("g-sidenav-pinned");
    }
  },

  mounted() {
    const code = getMerchantCode();
    this.merchantCode = code ? `/${code}` : "";
  },
  methods: {
    validityText,
    voucherCountText,
    ...mapMutations(["toggleEveryDisplay", "toggleHideConfig", "toggleNavbarOnly"]),
    ...mapActions(["setHeaderColor", "setLabelColor", "setButtonColor"]),

    // `.nav-item` is the sidenav's class too, so the tab state is data-driven now
    changeType(type) {
      this.type = type;
      this.myRewards = type == 1 ? this.rewardsUnused : this.rewardsUsed;
    },

    getVoucherBundleDetail() {
      // this.closeSnackbar()
      this.showLoader();
      var dataForm = {
        id: this.$route.params.id,
        member_voucher_bundle_id: this.memberVoucherBundleId,
        is_reward: this.isReward,
      };

      RewardService.getVoucherBundleDetail(dataForm).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.voucherBundleDetail = response.data.data.voucher;
            this.merchantMember = response.data.data.merchant_member;
            this.rewardsUsed = response.data.data.voucher_in_bundle_used;
            this.rewardsUnused = response.data.data.voucher_in_bundle_unused;
            this.myRewards = this.rewardsUnused;
            // keep the tab in step with the list a refetch just installed
            this.type = 1;
            this.isShowLayout = true;
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    processExchange() {
      $(".btn-close-confirm-modal").click();
      this.closeSnackbar();
      this.showLoader();
      const rewardId = this.voucherBundleDetail.id ?? 0;
      RewardService.processExchangeVoucherBundle(rewardId).then(
        (response) => {
          if (response.data.result.isSuccess) {
            $(".btn-close").click();
            this.$router.push({path: this.$route.path})
            this.getVoucherBundleDetail();
            $(".btn-close").click();
            $(".btn-success-modal").click();
          } else {
            $(".btn-close").click();
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    getRewardDetail(rewardId, expiry_date_text = null) {
      this.showLoader();
      RewardService.getRewardDetail(rewardId, 1).then(
        (response) => {
          if (response.data.result.isSuccess) {
            let rewardData = response.data.data.reward;
            if (expiry_date_text) {
              rewardData.expiry_date_text = expiry_date_text;
            }
            this.rewardDetail = rewardData;
            this.merchantMember = response.data.data.merchant_member;
            $(".btn-close").click();
            $(".btn-qrcode-modal").click();
          } else {
            this.snackbar = "danger";
            this.message = errorMessage(response.data.result.message);
          }
          this.hideLoader();
        },
        (error) => {
          this.snackbar = "danger";
          this.message = errorMessage(error);
          this.hideLoader();
        }
      );
    },

    gotoMyReward() {
      $(".btn-close").click();
      this.$router.push(this.merchantCode + "/my-rewards");
    },

    exchange() {
      this.keyReloadExchange = Math.random();
    },

    confirmExchange() {
      $(".btn-confirm-modal").click();
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    async landing() {
      $(".btn-close").click();
      this.$router.go(-1);
    },
  },
};
</script>
<style scoped>
/* MBR037 shell. This block used to be `<style scope>` — a typo, so none of it was scoped and
   every rule below leaked app-wide. `.btn-exchange` was the only rule other screens depended
   on; it now lives in assets/css/custom.css as an explicit global. */
/* .page-header is only as tall as its content, so a short bundle left the app's grey showing
   below the canvas. Full viewport, not min-vh-screen: that subtracts the navbar this screen hides. */
.mbr-bundle.page-header {
  min-height: 100vh;
  min-height: 100dvh;
  padding-bottom: 32px;
  font-family: var(--mbr-font-body);
}

/* Landing/Store/RewardCatalogue ship an unscoped `* { font-family: Poppins }`; `*` targets every
   element directly so inheritance never reaches them. :where() keeps this at zero specificity
   so the class rules below still win. */
.mbr-bundle :where(:not(.material-icons-round)) {
  font-family: var(--mbr-font-body);
}

.mbr-bundle .mask {
  background: var(--mbr-color-canvas) !important;
  opacity: 1;
}

/* mobile column, like the other MBR screens */
.mbr-bundle .container {
  max-width: 440px;
  margin: 0 auto;
}

/* ---- MBR038 header + summary card ---- */
.mbr-bundle__bar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 24px 20px 16px;
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-bottom: 1px solid rgba(var(--mbr-color-outline-rgb), 0.3);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-bundle__back,
.mbr-bundle__spacer {
  flex: none;
  width: 40px;
  height: 40px;
}

.mbr-bundle__back {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  color: var(--mbr-color-ink-headline);
  transition: transform 0.15s ease;
}

.mbr-bundle__back:active {
  transform: scale(0.95);
}

.mbr-bundle__back svg {
  width: 20px;
  height: 20px;
}

/* font-family/color set explicitly: Material Dashboard forces Roboto Slab on h1-h3 */
.mbr-bundle__title {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 17px;
  font-weight: 800;
  line-height: 24px;
  letter-spacing: -0.01em;
  color: var(--mbr-color-ink-headline);
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mbr-bundle__body {
  /* bottom room for the fixed Exchange bar */
  padding: 16px 20px 96px;
}

.mbr-bundle__summary {
  padding: 20px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: 22px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-bundle__head {
  display: flex;
  align-items: center;
  gap: 16px;
}

.mbr-bundle__icon {
  flex: none;
  width: 80px;
  height: 80px;
  object-fit: cover;
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.4);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-bundle__head-text {
  flex: 1 1 auto;
  min-width: 0;
}

.mbr-bundle__pill {
  display: inline-block;
  margin-bottom: 4px;
  padding: 2px 8px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 10px;
  font-weight: 800;
  line-height: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mbr-color-primary);
}

.mbr-bundle__name {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 17px;
  font-weight: 800;
  line-height: 22px;
  letter-spacing: -0.01em;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__meta {
  margin: 2px 0 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 17px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__totals {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--mbr-color-tint-dark);
}

.mbr-bundle__label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__count {
  display: block;
  margin-top: 2px;
  font-family: var(--mbr-font-headline);
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__ring-wrap {
  flex: none;
  display: flex;
  align-items: center;
  gap: 10px;
}

.mbr-bundle__ring {
  position: relative;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mbr-bundle__ring svg {
  width: 48px;
  height: 48px;
  transform: rotate(-90deg);
}

.mbr-bundle__ring-track {
  color: var(--mbr-color-tint-dark);
}

.mbr-bundle__ring-fill {
  color: var(--mbr-color-primary);
}

.mbr-bundle__ring-value {
  position: absolute;
  font-family: var(--mbr-font-headline);
  font-size: 11px;
  font-weight: 800;
  color: var(--mbr-color-primary);
}

.mbr-bundle__ring-left {
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-bundle__ring-used {
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__price {
  flex: none;
  text-align: right;
}

.mbr-bundle__price-amount {
  font-family: var(--mbr-font-headline);
  font-size: 18px;
  font-weight: 800;
  line-height: 24px;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__price-points {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-family: var(--mbr-font-headline);
  font-size: 14px;
  font-weight: 700;
  color: var(--mbr-color-primary);
}

/* ---- MBR039 tabs, voucher list, what's inside, T&C, CTA ---- */
.mbr-bundle__tabs {
  display: flex;
  margin-top: 20px;
  padding: 4px;
  background: var(--mbr-color-surface);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.6);
  border-radius: var(--mbr-radius-full);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-bundle__tab {
  flex: 1 1 0;
  padding: 8px 4px;
  background: none;
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__tab--active {
  background: var(--mbr-color-primary);
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.15);
}

.mbr-bundle__section {
  margin-top: 20px;
}

.mbr-bundle__section-label {
  display: block;
  padding: 0 4px;
  font-family: var(--mbr-font-headline);
  font-size: 11px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.mbr-bundle__voucher {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.mbr-bundle__voucher:hover {
  border-color: rgba(var(--mbr-color-primary-rgb), 0.4);
}

.mbr-bundle__voucher-no {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 13px;
  font-weight: 800;
  color: var(--mbr-color-primary);
}

.mbr-bundle__voucher-text {
  flex: 1 1 auto;
  min-width: 0;
}

.mbr-bundle__voucher-name {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 14px;
  font-weight: 800;
  line-height: 19px;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__voucher-valid {
  margin: 2px 0 0;
  font-size: 11px;
  font-weight: 500;
  line-height: 15px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__chevron {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--mbr-color-primary);
}

.mbr-bundle__tag {
  flex: none;
  padding: 3px 10px;
  border: 1px solid var(--mbr-color-outline);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 11px;
  font-weight: 700;
  line-height: 15px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__tag--locked {
  border-color: var(--mbr-color-gold);
  color: var(--mbr-color-gold);
}

.mbr-bundle__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding: 24px;
  background: rgba(var(--mbr-color-tint-rgb), 0.7);
  border: 1px dashed var(--mbr-color-outline);
  border-radius: 20px;
  text-align: center;
}

.mbr-bundle__empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: var(--mbr-color-peach);
  border-radius: var(--mbr-radius-full);
  color: var(--mbr-color-primary);
}

.mbr-bundle__empty-icon svg {
  width: 24px;
  height: 24px;
}

.mbr-bundle__empty-title {
  margin: 0;
  font-family: var(--mbr-font-headline);
  font-size: 14px;
  font-weight: 800;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__empty-text {
  max-width: 220px;
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 17px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__card {
  margin-top: 20px;
  padding: 20px;
  background: var(--mbr-color-card);
  border: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  border-radius: 22px;
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.05);
}

.mbr-bundle__heading {
  margin: 0 0 12px;
  font-family: var(--mbr-font-headline);
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__inside {
  margin: 0;
  padding: 0;
  list-style: none;
}

.mbr-bundle__inside li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
}

.mbr-bundle__inside li + li {
  border-top: 1px solid var(--mbr-color-tint-dark);
}

.mbr-bundle__inside-name {
  font-size: 13px;
  font-weight: 600;
  line-height: 19px;
  color: var(--mbr-color-ink-headline);
}

.mbr-bundle__inside-qty {
  flex: none;
  padding: 2px 10px;
  background: var(--mbr-color-tint);
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 12px;
  font-weight: 700;
  color: var(--mbr-color-primary);
}

.mbr-bundle__terms {
  margin: 0;
  padding-left: 18px;
  list-style: disc;
}

.mbr-bundle__terms li {
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  color: var(--mbr-color-ink-secondary);
}

.mbr-bundle__terms li + li {
  margin-top: 6px;
}

.mbr-bundle__cta {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 25;
  width: 100%;
  padding: 12px 20px calc(16px + env(safe-area-inset-bottom));
  background: rgba(var(--mbr-color-canvas-rgb), 0.95);
  border-top: 1px solid rgba(var(--mbr-color-outline-rgb), 0.5);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.mbr-bundle__exchange {
  display: block;
  width: 100%;
  max-width: 400px;
  height: 48px;
  margin: 0 auto;
  background: var(--mbr-color-primary);
  border: 0;
  border-radius: var(--mbr-radius-full);
  font-family: var(--mbr-font-headline);
  font-size: 15px;
  font-weight: 800;
  color: var(--mbr-color-on-primary);
  box-shadow: 0 1px 2px rgba(var(--mbr-color-primary-rgb), 0.15);
  transition: transform 0.12s ease;
}

.mbr-bundle__exchange:active {
  transform: scale(0.98);
}
</style>
